import { createSlice, nanoid } from "@reduxjs/toolkit";
import { fetchDataEntries, saveBulkDataEntries, updateDataEntry, deleteDataEntry } from "./dataEntriesThunks";
import { loadLS, saveLS } from "../../../../utils/storage";

const now = new Date();
const currentMonth = now.getMonth() + 1;
const currentYear = now.getFullYear();

const initialState = {
  items: [],
  drafts: loadLS("syp.veri_girisi.drafts", []),
  periods: loadLS("syp.veri_girisi.periods", {
    avans: { month: currentMonth, year: currentYear },
    mesai: { month: currentMonth, year: currentYear },
    kesinti: { month: currentMonth, year: currentYear },
  }),
  loading: false,
  error: null,
};

const TAB_TYPES = {
  avans: 1, // Advance
  mesai: 2, // Overtime
  kesinti: 3, // AbsenceDay
};

const dataEntriesSlice = createSlice({
  name: "dataEntries",
  initialState,
  reducers: {
    addDraftRow: (state, action) => {
      const { tab } = action.payload;
      const dataType = TAB_TYPES[tab];
      const { month, year } = state.periods[tab];
      state.drafts.push({
        id: `draft-${nanoid(8)}`,
        employeeId: "",
        value: "",
        description: "",
        dataType,
        month,
        year,
      });
      saveLS("syp.veri_girisi.drafts", state.drafts);
    },
    updateDraftRow: (state, action) => {
      const { id, field, value } = action.payload;
      const draft = state.drafts.find((d) => d.id === id);
      if (draft) {
        draft[field] = value;
        saveLS("syp.veri_girisi.drafts", state.drafts);
      }
    },
    deleteDraftRow: (state, action) => {
      const { id } = action.payload;
      state.drafts = state.drafts.filter((d) => d.id !== id);
      saveLS("syp.veri_girisi.drafts", state.drafts);
    },
    clearDrafts: (state, action) => {
      const { tab } = action.payload;
      const dataType = TAB_TYPES[tab];
      const { month, year } = state.periods[tab];
      state.drafts = state.drafts.filter((d) => !(d.dataType === dataType && d.month === month && d.year === year));
      saveLS("syp.veri_girisi.drafts", state.drafts);
    },
    setPeriod: (state, action) => {
      const { tab, month, year } = action.payload;
      state.periods[tab] = { month, year };
      saveLS("syp.veri_girisi.periods", state.periods);
    },
    updateLocalItem: (state, action) => {
       const { id, field, value } = action.payload;
       const item = state.items.find((i) => i.id === id);
       if (item) {
           item[field] = value;
           item._modified = true;

           // Persist this edit in the modified dictionary
           const modifiedDict = loadLS("syp.veri_girisi.modified", {});
           modifiedDict[id] = {
             value: item.value,
             description: item.description,
             dataType: item.dataType,
             month: item.month,
             year: item.year
           };
           saveLS("syp.veri_girisi.modified", modifiedDict);
       }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchDataEntries.pending, (state) => { state.loading = true; state.error = null; });
    builder.addCase(fetchDataEntries.fulfilled, (state, action) => {
      state.loading = false;
      const apiItems = Array.isArray(action.payload) ? action.payload : (action.payload?.items || []);
      
      // Load unsaved edits from localStorage
      const modifiedDict = loadLS("syp.veri_girisi.modified", {});

      state.items = apiItems.map((item) => {
        if (modifiedDict[item.id]) {
          return {
            ...item,
            value: modifiedDict[item.id].value,
            description: modifiedDict[item.id].description,
            _modified: true,
          };
        }
        return item;
      });
    });
    builder.addCase(fetchDataEntries.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(saveBulkDataEntries.fulfilled, (state) => {
      state.drafts = [];
      saveLS("syp.veri_girisi.drafts", []);
    });

    builder.addCase(deleteDataEntry.fulfilled, (state, action) => {
      const deletedId = action.payload;
      state.items = state.items.filter(i => i.id !== deletedId);
      
      const modifiedDict = loadLS("syp.veri_girisi.modified", {});
      if (modifiedDict[deletedId]) {
        delete modifiedDict[deletedId];
        saveLS("syp.veri_girisi.modified", modifiedDict);
      }
    });

    builder.addCase(updateDataEntry.fulfilled, (state, action) => {
      const savedItem = action.payload;
      const item = state.items.find(i => i.id === savedItem.id);
      if (item) item._modified = false;
      
      const modifiedDict = loadLS("syp.veri_girisi.modified", {});
      if (modifiedDict[savedItem.id]) {
        delete modifiedDict[savedItem.id];
        saveLS("syp.veri_girisi.modified", modifiedDict);
      }
    });
  }
});

export const { addDraftRow, updateDraftRow, deleteDraftRow, clearDrafts, setPeriod, updateLocalItem } = dataEntriesSlice.actions;
export default dataEntriesSlice.reducer;
